import { GET_CONTACT_BY_ID, GetContactByIdQuery } from '@zaplify/graphql';
import { MessageType } from '../../../types/message-type';
import { ComposerMessage } from './composer-message';
import { useLinkedin } from '../../use-linkedin';
import { useEmail } from '../../use-email';
import { useIsMobile } from '@shadcn/ui/hooks/use-mobile';
import { useCallback, useMemo } from 'react';
import { DisabledPrimaryActionReason } from './disabled-primary-action-reason';
import { ChannelType } from '../../../types/channel-type';
import { getEmailState } from '../../../functions/email-state';
import { ChannelAccountStatus } from '@zaplify/channel-accounts-shared';
import { useQuery } from '@apollo/client';

export interface ValidationResult {
    valid: boolean;
    reason?: DisabledPrimaryActionReason;
}

export const useMessageValidation = (
    messageType: MessageType,
    activeMessage: ComposerMessage,
    contactId: string,
    connectionStatus: string | undefined
) => {
    const { isPremium, extensionStatus } = useLinkedin();
    const { emailStatus } = useEmail();
    const isMobile = useIsMobile();
    const { data: contactData, loading: isLoadingContact } = useQuery(GET_CONTACT_BY_ID, {
        variables: { id: contactId },
    });
    const contact = useMemo(() => contactData?.Contacts_by_pk, [contactData?.Contacts_by_pk]);

    const isValidMessage = useCallback((): ValidationResult => {
        if (
            isMobile &&
            (messageType === MessageType.linkedinMessage || messageType === MessageType.linkedinConnectionRequest)
        ) {
            return { valid: false, reason: DisabledPrimaryActionReason.LINKEDIN_NOT_SUPPORTED_ON_MOBILE };
        }

        if (messageType === MessageType.linkedinMessage && !activeMessage.content?.trim().length) {
            return { valid: false, reason: DisabledPrimaryActionReason.MESSAGE_EMPTY };
        }

        if (messageType === MessageType.emailMessage) {
            if (!activeMessage.content?.length) {
                return { valid: false, reason: DisabledPrimaryActionReason.MESSAGE_EMPTY };
            }
            if (!activeMessage.subjectLine?.length) {
                return { valid: false, reason: DisabledPrimaryActionReason.SUBJECT_LINE_EMPTY };
            }
        }

        if (messageType === MessageType.linkedinConnectionRequest) {
            const limit = isPremium ? 300 : 200;
            if (activeMessage.content?.length > limit) {
                return { valid: false, reason: DisabledPrimaryActionReason.MESSAGE_TOO_LONG };
            }
        }

        return { valid: true };
    }, [messageType, activeMessage, isPremium]);

    const isValidRecipient = useCallback((): ValidationResult => {
        if (isLoadingContact) {
            return { valid: true };
        }
        if (isMobile && activeMessage.channelType === ChannelType.LINKEDIN) {
            return { valid: false, reason: DisabledPrimaryActionReason.LINKEDIN_NOT_SUPPORTED_ON_MOBILE };
        }
        if (activeMessage.channelType === ChannelType.LINKEDIN) {
            if (!contact?.linkedinProfileUrl || !contact.linkedinUserId) {
                return { valid: false, reason: DisabledPrimaryActionReason.RECIPIENT_MISSING_LINKEDIN_PROFILE };
            }
            if (connectionStatus === 'pending') {
                return { valid: false, reason: DisabledPrimaryActionReason.RECIPIENT_CONNECTION_PENDING };
            }
        }

        if (activeMessage.channelType === ChannelType.EMAIL && getEmailState(contact?.email) !== 'Email exists') {
            return { valid: false, reason: DisabledPrimaryActionReason.RECIPIENT_INVALID_EMAIL };
        }

        return { valid: true };
    }, [activeMessage.channelType, contact, connectionStatus, isMobile, isLoadingContact]);

    const isValidConfiguration = useCallback((): ValidationResult => {
        if (activeMessage.channelType === ChannelType.LINKEDIN) {
            if (extensionStatus === 'NOT_INSTALLED') {
                return { valid: false, reason: DisabledPrimaryActionReason.EXTENSION_NOT_INSTALLED };
            }
            if (extensionStatus === 'MISSING_CONFIG') {
                return { valid: false, reason: DisabledPrimaryActionReason.LINKEDIN_MISSING_CONFIG };
            }
            if (extensionStatus === 'NOT_AUTHENTICATED') {
                return { valid: false, reason: DisabledPrimaryActionReason.LINKEDIN_NOT_AUTHENTICATED };
            }
            if (!connectionStatus || connectionStatus === 'syncing') {
                return { valid: false, reason: DisabledPrimaryActionReason.LINKEDIN_SYNCING };
            }
        }

        if (activeMessage.channelType === ChannelType.EMAIL) {
            if (!emailStatus) {
                return { valid: false, reason: DisabledPrimaryActionReason.EMAIL_NOT_CONNECTED };
            }
            if (emailStatus === ChannelAccountStatus.NOT_AUTHENTICATED) {
                return { valid: false, reason: DisabledPrimaryActionReason.EMAIL_NOT_AUTHENTICATED };
            }
            if (emailStatus === ChannelAccountStatus.SCOPES) {
                return { valid: false, reason: DisabledPrimaryActionReason.EMAIL_SCOPES };
            }
        }

        return { valid: true };
    }, [activeMessage.channelType, extensionStatus, emailStatus, connectionStatus]);

    return {
        isValidMessage,
        isValidRecipient,
        isValidConfiguration,
    };
};
